<template>
    <div class="conversation" v-if="conversation">
        <div class="columns is-gapless">
            <div class="conversation-list column is-3-desktop" :class="{'is-hidden-touch': !showingConversationList}">
                <div class="box is-radiusless is-marginless has-background-primary has-text-white has-text-centered is-shadowless">
                    <span class="is-pulled-right is-hidden-desktop button is-outlined is-white"
                          @click="showChatList"><b-icon icon="close" /></span>
                    <h1 class="is-size-5">Projekter</h1>
                </div>

                <div class="columns is-gapless is-fullheight">
                    <div class="column box is-radiusless is-paddingless is-scrollable">
                        <conversation-list :initial-conversation="conversation" />
                    </div>
                </div>
            </div>

            <div class="conversation-chat column is-6-desktop is-12-touch" :class="{'is-hidden-touch': !showingChatList}">
                <div class="box is-radiusless is-marginless has-background-primary has-text-white has-text-centered is-shadowless">
                    <span class="is-pulled-right is-hidden-desktop button is-outlined is-white"
                          @click="showDetails"><b-icon icon="calendar-text" /></span>

                    <span class="is-pulled-left is-hidden-desktop button is-outlined is-white"
                          @click="showConversationList"><b-icon icon="chat" /></span>

                    <h2 class="is-size-5">{{this.title}}</h2>
                </div>

                <div class="columns is-gapless is-fullheight">
                    <div class="column chat-wrapper box is-radiusless is-marginless is-paddingless">
                        <div ref="chat" class="chat-list-wrapper">
                            <message-list @change="scrollChatToBottom" />
                        </div>

                        <div class="message-form-wrapper">
                            <message-form :conversation="conversation" />
                        </div>
                    </div>
                </div>
            </div>

            <div class="conversation-details column is-3-desktop" :class="{'is-hidden-touch': !showingDetails}">
                <div class="box is-radiusless is-marginless has-background-primary has-text-white has-text-centered is-shadowless">
                    <span class="is-pulled-left is-hidden-desktop button is-outlined is-white"
                          @click="showChatList"><b-icon icon="close" /></span>
                    <h1 class="is-size-5">&nbsp;</h1>
                </div>

                <div class="columns is-gapless is-fullheight">
                    <div class="column box is-radiusless is-paddingless is-scrollable">
                        <template v-if="conversation.project">
                            <project-attachments :project="conversation.project" />

                            <project-details :project="conversation.project" />

                            <project-actions :project="conversation.project" />

                            <project-events :project="conversation.project" />
                        </template>

                        <div v-else class="details box is-radiusless is-marginless is-shadowless">
                            <h4 class="is-size-6">Om samtalen</h4>
                            <br>

                            <p>Dette er en samtale med {{this.isSender ? conversation.addressee.name : conversation.sender.name}}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import ConversationList from '@/taskflex/conversations/components/ConversationList';
    import { mapGetters, mapActions } from 'vuex';
    import Message from '@/taskflex/conversations/components/Messages/Message';
    import MessageForm from '@/taskflex/conversations/components/Messages/MessageForm';
    import MessageList from '@/taskflex/conversations/components/Messages/MessageList';
    import PageTop from '@/global/components/PageTop';
    import ProjectDetails from '@/taskflex/conversations/components/Projects/ProjectDetails';
    import ProjectEvents from '@/taskflex/conversations/components/Projects/ProjectEvents';
    import ProjectActions from '@/taskflex/conversations/components/Projects/ProjectActions';
    import ProjectAttachments from '@/taskflex/conversations/components/Projects/ProjectAttachments';

    export default {
        components: {
            MessageList,
            ConversationList,
            Message,
            MessageForm,
            PageTop,
            ProjectDetails,
            ProjectEvents,
            ProjectActions,
            ProjectAttachments
        },
        data() {
            return {
                showingConversationList: false,
                showingChatList: true,
                showingDetails: false,
            };
        },

        computed: {
            conversationID() {
                const { id = null } = this.$route.params;

                return id;
            },

            ...mapGetters('auth', [
                'isLoggedIn',
                'currentUserAccount',
            ]),

            ...mapGetters('conversations', {
                conversation: 'activeConversation',
                isLoading: 'isLoadingConversation',
            }),

            title() {
                if (!this.conversation) {
                    return 'blank';
                }

                const {
                    conversation: {
                        project,
                        addressee,
                        sender,
                    },
                } = this;

                if (!project) {
                    return `Samtale med ${this.isSender ? addressee.name : sender.name}`;
                }

                return project.name;
            },

            isSender() {
                if (!this.conversation) {
                    return 'blank';
                }

                const {
                    sender,
                } = this.conversation;

                return sender.id === this.currentUserAccount.id;
            },
        },
        methods: {
            ...mapActions('conversations', [
                'loadConversation',
            ]),

            showConversationList() {
                this.showingChatList = false;
                this.showingConversationList = true;
                this.showingDetails = false;
            },

            showDetails() {
                this.showingChatList = false;
                this.showingConversationList = false;
                this.showingDetails = true;
            },

            showChatList() {
                this.showingChatList = true;
                this.showingConversationList = false;
                this.showingDetails = false;
            },

            scrollChatToBottom() {
                const { chat } = this.$refs;

                chat.scrollTop = chat.scrollHeight;
            },
        },
        watch: {
            isLoggedIn() {
                this.loadConversation(this.conversationID);

                this.showChatList();
            },

            conversationID() {
                this.loadConversation(this.conversationID);

                this.showChatList();
            },
        },

        mounted() {
            this.$emit('disable-footer');

            if (this.isLoggedIn) {
                this.loadConversation(this.conversationID);
            }

            this.showChatList();

            this.$eventHub.$on('conversations.messages.new', this.scrollChatToBottom);
        },

        beforeDestroy() {
            this.$emit('enable-footer');
            this.$eventHub.$off('conversations.messages.new', this.scrollChatToBottom);
        }
    };
</script>


<style lang="scss" scoped>
    @import '../../../global/styles/variables';

    .conversation {
        background: $background-blueish;

        $navbar-height: 52px;
        $header-height: 64px;

        .columns {
            &.is-fullheight {
                > .column {
                    min-height: calc( 100vh - #{$navbar-height + $header-height});
                    max-height: calc( 100vh - #{$navbar-height + $header-height});
                    height: calc( 100vh - #{$navbar-height + $header-height});
                }
            }

            .is-scrollable {
                overflow-y: auto
            }
        }

        .chat-wrapper {
            flex-direction: column;
            display: flex;

            .chat-list-wrapper {
                flex: 2;
                padding: 0.75rem;
                overflow-y: auto
            }

            .message-form-wrapper {
                box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);
                padding: 0.75rem;
                min-height: 100px;
            }
        }
    }
</style>



// WEBPACK FOOTER //
// src/taskflex/conversations/pages/Conversation.vue